import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FileUpload from "@/components/FileUpload";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
    ArrowRight,
    Car,
    ShieldCheck,
    BadgeCheck,
    FileCheck,
    Clock,
    X,
    ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";

const CITIES = ["Kingston", "Portmore", "Spanish Town", "Montego Bay", "Ocho Rios", "Mandeville"];

/**
 * SMART Wheels — dedicated driver-signup page.
 *
 * Submits to the same /providers/apply endpoint as the general Pro flow but:
 *   - service_categories forced to ["Transport"]
 *   - Driver's License upload is MANDATORY (CEO directive)
 *   - Captures vehicle make/model/year/plate + insurance + road licence + PPV
 *
 * Unauthenticated visitors are bounced to /auth?role=provider&mode=register&next=/wheels/apply
 * exactly like the general /become-provider flow, to avoid the 401-on-file-upload bug.
 */
export default function WheelsApplyPage() {
    const navigate = useNavigate();
    const { user, refresh } = useAuth();

    const [authChecked, setAuthChecked] = useState(false);
    const [profile, setProfile] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    // Driver core
    const [bio, setBio] = useState("");
    const [yearsDriving, setYearsDriving] = useState(3);
    const [hourlyRate, setHourlyRate] = useState(3500);
    const [locations, setLocations] = useState(["Kingston"]);

    // Vehicle info
    const [vehicleMakeModel, setVehicleMakeModel] = useState("");
    const [vehicleYear, setVehicleYear] = useState("");
    const [vehiclePlate, setVehiclePlate] = useState("");

    // Required documents
    const [profilePic, setProfilePic] = useState("");
    const [idDoc, setIdDoc] = useState("");
    const [driverLicense, setDriverLicense] = useState("");
    const [vehicleReg, setVehicleReg] = useState("");
    const [insuranceCert, setInsuranceCert] = useState("");
    const [roadLicence, setRoadLicence] = useState("");
    const [trn, setTrn] = useState("");
    const [proofOfAddress, setProofOfAddress] = useState("");

    // Optional / 90-day grace
    const [policeRecord, setPoliceRecord] = useState("");
    const [ppvPermit, setPpvPermit] = useState("");

    // Auth gate: unauthenticated → redirect to register
    useEffect(() => {
        if (user === null) {
            navigate("/auth?role=provider&mode=register&next=/wheels/apply", { replace: true });
            return;
        }
        if (user) setAuthChecked(true);
    }, [user, navigate]);

    // Prefill from existing application
    useEffect(() => {
        if (!user) return;
        api.get("/providers/me")
            .then(({ data }) => {
                setProfile(data);
                setBio(data.bio || "");
                setYearsDriving(data.years_driving || data.years_experience || 3);
                setHourlyRate(data.hourly_rate || 3500);
                setLocations(data.locations?.length ? data.locations : ["Kingston"]);
                setVehicleMakeModel(data.vehicle_make_model || "");
                setVehicleYear(data.vehicle_year || "");
                setVehiclePlate(data.vehicle_plate || "");
                setProfilePic(data.profile_picture_url || "");
                setIdDoc(data.id_document_url || "");
                setDriverLicense(data.driver_license_url || "");
                setVehicleReg(data.vehicle_registration_url || "");
                setInsuranceCert(data.insurance_certificate_url || "");
                setRoadLicence(data.road_licence_url || "");
                setTrn(data.trn_url || "");
                setProofOfAddress(data.proof_of_address_url || "");
                setPoliceRecord(data.police_record_url || "");
                setPpvPermit(data.ppv_permit_url || "");
            })
            .catch(() => {});
    }, [user]);

    const toggleLocation = (c) => {
        if (locations.includes(c)) setLocations(locations.filter((v) => v !== c));
        else setLocations([...locations, c]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!user) {
            navigate("/auth?role=provider&mode=register&next=/wheels/apply");
            return;
        }

        // Required field gating (CEO directive: License is MANDATORY)
        if (!profilePic) return toast.error("Profile picture is required.");
        if (!idDoc) return toast.error("Government ID is required.");
        if (!driverLicense) return toast.error("Driver's License photo is REQUIRED to drive on SMART Wheels.");
        if (!vehicleReg) return toast.error("Vehicle Registration (certificate of fitness or title) is required.");
        if (!insuranceCert) return toast.error("Vehicle Insurance Certificate is required.");
        if (!roadLicence) return toast.error("Road Licence is required.");
        if (!trn) return toast.error("TRN document is required for tax-compliant payouts.");
        if (!proofOfAddress) return toast.error("Proof of Address is required.");
        if (!vehicleMakeModel.trim()) return toast.error("Tell us your vehicle make and model.");
        if (!vehiclePlate.trim()) return toast.error("Vehicle licence plate is required.");
        if (locations.length === 0) return toast.error("Pick at least one service area.");

        setSubmitting(true);
        try {
            const { data } = await api.post("/providers/apply", {
                bio: bio || `SMART Wheels driver — ${yearsDriving} years on the road.`,
                skills: ["Driving", "Airport Transfer", "Driver-for-day"],
                service_categories: ["Transport"],
                hourly_rate: parseFloat(hourlyRate),
                locations,
                profile_picture_url: profilePic,
                id_document_url: idDoc,
                police_record_url: policeRecord || null,
                trn_url: trn,
                proof_of_address_url: proofOfAddress,
                driver_license_url: driverLicense,
                vehicle_registration_url: vehicleReg,
                insurance_certificate_url: insuranceCert,
                road_licence_url: roadLicence,
                ppv_permit_url: ppvPermit || null,
                years_driving: parseInt(yearsDriving) || 0,
                vehicle_make_model: vehicleMakeModel,
                vehicle_year: parseInt(vehicleYear) || null,
                vehicle_plate: vehiclePlate.toUpperCase().trim(),
                certificates: [],
                portfolio: [],
                years_experience: parseInt(yearsDriving) || 0,
            });
            setProfile(data);
            await refresh();
            const level = data.verification_level;
            if (level === "royal") {
                toast.success("All Wheels docs in — you're now Fully Royal Verified.");
            } else if (level === "provisional") {
                toast.success("You're Provisionally Verified — start accepting trips. Upload your Police Certificate within 90 days to upgrade to Royal.");
            } else {
                toast.success("Wheels application updated.");
            }
            setTimeout(() => navigate("/provider"), 1400);
        } catch (e2) {
            toast.error(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
        } finally {
            setSubmitting(false);
        }
    };

    if (!authChecked || !user) {
        return (
            <div
                className="min-h-screen bg-background grid place-items-center"
                data-testid="wheels-apply-redirecting"
            >
                <div className="text-center">
                    <div className="overline mb-3">SMART Wheels</div>
                    <h1 className="font-serif text-2xl">Taking you to sign-up…</h1>
                    <p className="mt-3 text-sm text-muted-foreground max-w-md mx-auto">
                        We're routing you through a 30-second registration first — file uploads need a Royal account to attach to.
                    </p>
                </div>
            </div>
        );
    }

    const statusBadge = profile?.application_status;

    return (
        <div className="min-h-screen bg-background" data-testid="wheels-apply-page">
            <Header />

            <section className="max-w-7xl mx-auto px-6 lg:px-10 py-12 lg:py-16">
                <div className="grid lg:grid-cols-12 gap-16">
                    {/* LEFT — pitch */}
                    <div className="lg:col-span-5">
                        <div className="flex items-center gap-2 mb-4">
                            <Car size={14} className="gold-text" strokeWidth={1.5} />
                            <div className="overline">SMART Wheels · Driver Signup</div>
                        </div>
                        <h1 className="font-serif text-5xl sm:text-6xl tracking-tighter leading-[0.95]">
                            Drive with<br />
                            <span className="italic gold-grad">SMART Wheels.</span>
                        </h1>
                        <p className="mt-8 text-base text-muted-foreground leading-relaxed">
                            Premium clients. Airport runs. Driver-for-day jobs.
                            Set your own hours and get paid weekly into any
                            Jamaican bank. <strong className="gold-text">No application fees, ever.</strong>
                        </p>

                        {/* Mandatory docs box */}
                        <div className="mt-10 p-6 bg-gradient-to-br from-primary/10 via-card to-card border border-primary/40 rounded-sm relative overflow-hidden" data-testid="wheels-required-docs">
                            <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full bg-primary/10 blur-2xl" />
                            <div className="flex items-center gap-2 mb-2 relative">
                                <FileCheck size={14} className="gold-text" strokeWidth={1.5} />
                                <div className="overline">Required to Drive</div>
                            </div>
                            <p className="text-xs text-muted-foreground mb-5 relative">
                                A valid Jamaican <strong className="gold-text">Driver's License</strong> is
                                mandatory. We will physically verify your license
                                photo against your Government ID before activation.
                            </p>
                            <ol className="space-y-3 relative">
                                {[
                                    "Profile picture",
                                    "Government ID (passport / national ID)",
                                    "Driver's License (front + back)",
                                    "Vehicle Registration (Cert. of Fitness / Title)",
                                    "Vehicle Insurance Certificate",
                                    "Road Licence",
                                    "Taxpayer Registration Number (TRN)",
                                    "Proof of Address (utility bill, last 3 months)",
                                ].map((title, i) => (
                                    <li key={`wheels-${title}`} className="flex gap-3" data-testid={`wheels-req-${i}`}>
                                        <span className="shrink-0 mt-0.5 w-6 h-6 grid place-items-center rounded-sm text-[10px] font-semibold bg-primary text-primary-foreground">
                                            {i + 1}
                                        </span>
                                        <div className="text-sm text-foreground font-medium leading-snug">
                                            {title}
                                        </div>
                                    </li>
                                ))}
                            </ol>
                            <div className="mt-6 pt-4 border-t border-white/10 flex gap-3 items-start" data-testid="wheels-grace-notice">
                                <Clock size={14} className="gold-text shrink-0 mt-0.5" strokeWidth={1.5} />
                                <p className="text-[11px] text-muted-foreground leading-relaxed">
                                    Optional but recommended within 90 days:
                                    <strong className="text-amber-300"> Police Certificate</strong> and
                                    <strong className="text-amber-300"> PPV Permit</strong> (if carrying paying passengers commercially). Upload to upgrade from Provisional to Fully Royal Verified status.
                                </p>
                            </div>
                        </div>

                        <div className="mt-6 p-6 bg-card border border-white/10 rounded-sm" data-testid="wheels-safety-box">
                            <div className="flex items-center gap-2 mb-3">
                                <ShieldAlert size={14} className="gold-text" strokeWidth={1.5} />
                                <div className="overline">Wheels Safety Policy</div>
                            </div>
                            <ul className="space-y-2 text-xs text-muted-foreground">
                                <li>• Trip-start &amp; trip-end pings logged automatically</li>
                                <li>• Missed safety pings escalate to admin &amp; emergency contacts</li>
                                <li>• Cash payments are NOT permitted — all fares settled in-app</li>
                                <li>• Royal Protection &amp; Insurance fee included on every trip</li>
                                <li>• 24/7 WhatsApp support: <span className="gold-text">+1 876-502-8155</span></li>
                            </ul>
                        </div>
                    </div>

                    {/* RIGHT — form */}
                    <div className="lg:col-span-7">
                        <form
                            onSubmit={handleSubmit}
                            className="bg-card border border-white/10 p-8 lg:p-10 rounded-sm space-y-6"
                            data-testid="wheels-apply-form"
                        >
                            <div>
                                <div className="overline mb-2">SMART Wheels Application</div>
                                <h2 className="font-serif text-3xl">Get behind the wheel.</h2>
                            </div>

                            {statusBadge && (
                                <ApplicationStatusBanner
                                    status={statusBadge}
                                    tier={profile?.tier}
                                    adminNotes={profile?.admin_notes}
                                />
                            )}

                            <Field label="About you (driving experience, languages, special services)">
                                <Textarea
                                    value={bio}
                                    onChange={(e) => setBio(e.target.value)}
                                    rows={3}
                                    placeholder="5 years driving in Kingston and Montego Bay. Familiar with Sangster Airport, all hotel circuits, and Negril. English + Patois."
                                    className="bg-input border-white/10 rounded-sm"
                                    data-testid="wheels-bio-input"
                                />
                            </Field>

                            <div className="grid sm:grid-cols-2 gap-4">
                                <Field label="Years driving (Jamaica)">
                                    <Input
                                        type="number"
                                        min="0"
                                        value={yearsDriving}
                                        onChange={(e) => setYearsDriving(e.target.value)}
                                        className="bg-input border-white/10 h-11 rounded-sm"
                                        required
                                        data-testid="wheels-years-input"
                                    />
                                </Field>
                                <Field label="Hourly rate (JMD)">
                                    <Input
                                        type="number"
                                        value={hourlyRate}
                                        onChange={(e) => setHourlyRate(e.target.value)}
                                        className="bg-input border-white/10 h-11 rounded-sm"
                                        required
                                        data-testid="wheels-rate-input"
                                    />
                                </Field>
                            </div>

                            <div className="hairline" />
                            <div className="overline">Your Vehicle</div>

                            <div className="grid sm:grid-cols-3 gap-4">
                                <Field label="Make &amp; Model *">
                                    <Input
                                        value={vehicleMakeModel}
                                        onChange={(e) => setVehicleMakeModel(e.target.value)}
                                        placeholder="Toyota Probox"
                                        className="bg-input border-white/10 h-11 rounded-sm"
                                        required
                                        data-testid="wheels-make-model-input"
                                    />
                                </Field>
                                <Field label="Year">
                                    <Input
                                        type="number"
                                        value={vehicleYear}
                                        onChange={(e) => setVehicleYear(e.target.value)}
                                        placeholder="2019"
                                        className="bg-input border-white/10 h-11 rounded-sm"
                                        data-testid="wheels-year-input"
                                    />
                                </Field>
                                <Field label="Plate *">
                                    <Input
                                        value={vehiclePlate}
                                        onChange={(e) => setVehiclePlate(e.target.value.toUpperCase())}
                                        placeholder="ABC1234"
                                        className="bg-input border-white/10 h-11 rounded-sm uppercase tracking-widest"
                                        required
                                        data-testid="wheels-plate-input"
                                    />
                                </Field>
                            </div>

                            <Field label="Service Areas">
                                <div className="flex flex-wrap gap-3 pt-2" data-testid="wheels-locations">
                                    {CITIES.map((c) => (
                                        <label
                                            key={c}
                                            className={`flex items-center gap-2 cursor-pointer px-4 py-2 border rounded-sm text-xs uppercase tracking-[0.2em] ${
                                                locations.includes(c)
                                                    ? "border-primary bg-primary/10 text-primary"
                                                    : "border-white/10 text-muted-foreground hover:text-foreground"
                                            }`}
                                            data-testid={`wheels-location-${c}`}
                                        >
                                            <Checkbox
                                                checked={locations.includes(c)}
                                                onCheckedChange={() => toggleLocation(c)}
                                                className="border-white/20"
                                            />
                                            {c}
                                        </label>
                                    ))}
                                </div>
                            </Field>

                            <div className="hairline" />
                            <div className="overline">Verification Documents</div>
                            <div className="text-[11px] text-muted-foreground -mt-3">
                                All documents are stored encrypted and only visible to SMART admins for verification.
                            </div>

                            <FileUpload
                                purpose="profile_pic"
                                value={profilePic}
                                onUploaded={(f) => setProfilePic(f.id)}
                                onCleared={() => setProfilePic("")}
                                label="Profile picture *"
                                accept="image/*"
                                testid="wheels-profile-pic"
                            />
                            <FileUpload
                                purpose="id_document"
                                value={idDoc}
                                onUploaded={(f) => setIdDoc(f.id)}
                                onCleared={() => setIdDoc("")}
                                label="Government ID * (passport / national ID)"
                                testid="wheels-id-doc"
                            />

                            {/* MANDATORY DRIVER'S LICENSE — CEO directive */}
                            <div className="p-1 -mx-1 rounded-sm bg-gradient-to-r from-primary/20 via-transparent to-primary/20">
                                <FileUpload
                                    purpose="driver_license"
                                    value={driverLicense}
                                    onUploaded={(f) => setDriverLicense(f.id)}
                                    onCleared={() => setDriverLicense("")}
                                    label="Driver's License * (mandatory — clear photo, front side)"
                                    accept="image/*"
                                    testid="wheels-driver-license"
                                />
                            </div>

                            <FileUpload
                                purpose="vehicle_registration"
                                value={vehicleReg}
                                onUploaded={(f) => setVehicleReg(f.id)}
                                onCleared={() => setVehicleReg("")}
                                label="Vehicle Registration * (Cert. of Fitness / Title)"
                                testid="wheels-vehicle-reg"
                            />
                            <FileUpload
                                purpose="insurance_certificate"
                                value={insuranceCert}
                                onUploaded={(f) => setInsuranceCert(f.id)}
                                onCleared={() => setInsuranceCert("")}
                                label="Vehicle Insurance Certificate *"
                                testid="wheels-insurance-cert"
                            />
                            <FileUpload
                                purpose="road_licence"
                                value={roadLicence}
                                onUploaded={(f) => setRoadLicence(f.id)}
                                onCleared={() => setRoadLicence("")}
                                label="Road Licence *"
                                testid="wheels-road-licence"
                            />
                            <FileUpload
                                purpose="trn"
                                value={trn}
                                onUploaded={(f) => setTrn(f.id)}
                                onCleared={() => setTrn("")}
                                label="TRN document *"
                                testid="wheels-trn"
                            />
                            <FileUpload
                                purpose="proof_of_address"
                                value={proofOfAddress}
                                onUploaded={(f) => setProofOfAddress(f.id)}
                                onCleared={() => setProofOfAddress("")}
                                label="Proof of Address * (utility bill, last 3 months)"
                                testid="wheels-proof-of-address"
                            />

                            <div className="hairline" />
                            <div className="overline">90-Day Grace Documents (Royal Tier)</div>
                            <div className="text-[11px] text-muted-foreground -mt-3">
                                Add these any time within your first 90 days to upgrade to Royal Verified status.
                            </div>

                            <FileUpload
                                purpose="police_record"
                                value={policeRecord}
                                onUploaded={(f) => setPoliceRecord(f.id)}
                                onCleared={() => setPoliceRecord("")}
                                label="Police Certificate (JCF Records / e-PRC, within last 6 months)"
                                testid="wheels-police-record"
                            />
                            <FileUpload
                                purpose="ppv_permit"
                                value={ppvPermit}
                                onUploaded={(f) => setPpvPermit(f.id)}
                                onCleared={() => setPpvPermit("")}
                                label="PPV Permit (if carrying paying passengers commercially)"
                                testid="wheels-ppv-permit"
                            />

                            <Button
                                type="submit"
                                disabled={
                                    submitting ||
                                    !driverLicense ||
                                    !profilePic ||
                                    !idDoc ||
                                    !vehicleReg ||
                                    !insuranceCert ||
                                    !roadLicence ||
                                    !trn ||
                                    !proofOfAddress ||
                                    locations.length === 0
                                }
                                className="w-full bg-primary text-primary-foreground rounded-sm uppercase tracking-[0.2em] text-xs h-12"
                                data-testid="wheels-submit-btn"
                            >
                                {submitting ? "Submitting..." : profile?.driver_license_url ? "Update Wheels Profile" : "Submit Wheels Application"}{" "}
                                <ArrowRight size={14} className="ml-2" strokeWidth={1.5} />
                            </Button>
                            <p className="text-[11px] text-muted-foreground">
                                By submitting you confirm that all documents are
                                genuine and current, you consent to background
                                verification with the JCF, and you agree to settle
                                every trip through the SMART app (no off-platform
                                cash). Operator: SMART DEMANDS 24/7 LIMITED.
                            </p>
                        </form>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <Label className="overline mb-2 block">{label}</Label>
            {children}
        </div>
    );
}

function ApplicationStatusBanner({ status, tier, adminNotes }) {
    const config = {
        pending: { label: "Pending Review", icon: ShieldAlert, color: "bg-amber-900/30 text-amber-300 border-amber-700/40" },
        under_review: { label: "Under Review", icon: ShieldCheck, color: "bg-blue-900/30 text-blue-300 border-blue-700/40" },
        approved: { label: "Approved", icon: BadgeCheck, color: "bg-emerald-900/30 text-emerald-300 border-emerald-700/40" },
        rejected: { label: "Rejected", icon: X, color: "bg-red-900/30 text-red-300 border-red-700/40" },
    }[status] || { label: status, icon: ShieldAlert, color: "bg-card text-muted-foreground border-white/10" };
    const Icon = config.icon;
    return (
        <div className={`flex items-start gap-3 p-4 border rounded-sm ${config.color}`} data-testid={`wheels-status-${status}`}>
            <Icon size={16} strokeWidth={1.5} className="mt-0.5 flex-shrink-0" />
            <div className="flex-1">
                <div className="text-sm font-semibold uppercase tracking-[0.2em]">{config.label}</div>
                {tier && status === "approved" && (
                    <div className="mt-1 text-xs">Assigned tier: <span className="gold-text uppercase tracking-[0.2em]">{tier}</span></div>
                )}
                {adminNotes && <div className="mt-1 text-xs opacity-90">Admin note: {adminNotes}</div>}
            </div>
        </div>
    );
}
